/*
 * Wizard Step 3 (U7 / Settings): per-launch defaults applied uniformly to every
 * selected ticket. The Page / Campaign / Adset inputs are searchable Mantine
 * `Select` comboboxes backed by Admanage proxy endpoints; URL stays a plain
 * `TextInput`. The four canonical field names (page_id / link /
 * target_campaign_id / source_adset_id) and the `LaunchDefaults` interface are
 * UNCHANGED so the wizard's call site (LaunchWizard) needs no edits.
 *
 * On the Preview step this component renders as a READ-ONLY SUMMARY (`disabled`)
 * atop the table — the run is already created from these values, so the page /
 * URL / campaign / adset are viewable but not editable. The Admanage queries
 * still run so the selects show the resolved labels, not raw ids.
 *
 * Content states per spec (no generic spinners):
 *   loading   → the Select shows a "Loading …" message in its dropdown
 *   424       → Field-level error OUTSIDE the dropdown (credential unavailable)
 *   502       → "Couldn't reach Admanage — try again"
 *   API empty → distinct message ("No active or scheduled campaigns" / "This
 *               campaign has no ad sets"), separate from filter no-match
 *   filter no-match → "No results for '<query>'"
 *   Adset disabled (no campaign chosen) → placeholder "Choose a campaign first"
 */

import { useEffect, useState } from "react";
import { Autocomplete, SegmentedControl, Select, Stack, Switch, Text, TextInput } from "@mantine/core";
import {
  useLaunchProfiles,
  useLaunchCampaigns,
  useLaunchAdsets,
  useLaunchLandingPages,
} from "../../api/queries";
import { ApiError } from "../../api/client";

export interface LaunchDefaults {
  page_id: string;
  link: string;
  target_campaign_id: string;
  source_adset_id: string;
  /**
   * OPTIONAL persona first name swapped for [NAME]-family placeholder tokens
   * in the copy during prep (2026-07-06 #5). Empty ⇒ no swap — leftover
   * placeholders hard-fail the ticket exactly as before. In the POV fan-out
   * each group carries its own value (each group ≈ one persona/page).
   */
  name_swap: string;
  /**
   * The scheduled go-live instant (2026-07-06 #8 — always-on: every wizard
   * launch lands the adsets in Meta's "Scheduled" state, bypassing the
   * per-page active-ad cap). Held in the input's LOCAL `datetime-local`
   * format (`YYYY-MM-DDTHH:mm`); converted to ISO-8601-with-offset at submit
   * ({@link localToIsoOffset}). Defaults to ~1.5 hours ahead
   * ({@link defaultStartTimeLocal}); must be strictly in the future.
   */
  start_time: string;
  /**
   * Per-launch bid choice (2026-07-17): "product" sends nothing (the product
   * config applies), "none" sends bid_amount:null (launch WITHOUT bid fields —
   * for campaigns that must not carry a cap), "custom" sends the amount below.
   */
  bid_mode: "product" | "none" | "custom";
  /** Custom cost/bid-cap amount (account currency) — used when bid_mode="custom". */
  bid_amount: string;
  /**
   * Per-launch optimization goal (2026-07-20): must match the TARGET
   * campaign's existing adsets (Meta requires one goal per lowest-cost
   * campaign, and the team's campaigns split between conversions and value).
   * "product" sends nothing — the product config applies.
   */
  opt_goal: "product" | "OFFSITE_CONVERSIONS" | "VALUE";
  /**
   * Combine mode (2026-07-31 — retargeting): when on, EVERY selected ticket's
   * ads launch into ONE ad set with the name below, instead of one ad set per
   * concept. Single-run launches only (the POV fan-out ignores it); the
   * backend requires the scheduled path (always true in the wizard).
   */
  combine_adsets: boolean;
  /** The shared ad set's exact name — required when combine_adsets is on. */
  combine_adset_name: string;
  /**
   * Launch into an existing ad set (2026-10-20): when on, the chosen Adset is
   * not a template — every ticket's ads are added to it and nothing new is
   * created. Off (the default) duplicates per concept as before.
   */
  into_existing_adset: boolean;
}

/** Pad to two digits (datetime-local / ISO formatting). */
function pad2(n: number): string {
  return String(n).padStart(2, "0");
}

/**
 * The default scheduled go-live: ~1.5 hours from now, rounded UP to the next
 * 5 minutes, in the local `datetime-local` input format.
 *
 * HISTORY (read before changing again): started at ~2h; DOUBLED to ~4h on
 * 2026-07-08 because the per-page active-ad cap bypass proved unreliable at
 * short lead times in production; shortened to ~1h on 2026-08-26 at Ben's
 * request for faster turnarounds; nudged to ~1.5h on 2026-09-24 (Ben). If
 * scheduled launches start hitting the per-page cap again, THIS is the
 * first suspect — bump it back up. Always adjustable per launch in the input.
 */
export function defaultStartTimeLocal(): string {
  const d = new Date(Date.now() + 90 * 60 * 1000);
  d.setMinutes(Math.ceil(d.getMinutes() / 5) * 5, 0, 0);
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}T${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
}

/** Valid bid choice: custom mode needs a positive number; other modes always pass. */
export function isValidBid(values: Pick<LaunchDefaults, "bid_mode" | "bid_amount">): boolean {
  if (values.bid_mode !== "custom") return true;
  const n = Number(values.bid_amount.trim());
  return Number.isFinite(n) && n > 0;
}

/** A parseable local datetime strictly in the future (mirrors the server's KTD-12 check). */
export function isValidStartTime(value: string): boolean {
  const v = value.trim();
  if (v === "") return false;
  const ms = new Date(v).getTime(); // datetime-local parses as LOCAL time
  return Number.isFinite(ms) && ms > Date.now();
}

/**
 * Convert the input's local `datetime-local` value to the STRICT
 * ISO-8601-with-offset string the server requires (KTD-12 rejects
 * offset-less instants — a silently-local-coerced time would schedule wrong).
 */
export function localToIsoOffset(value: string): string {
  const d = new Date(value);
  const offMin = -d.getTimezoneOffset(); // minutes EAST of UTC
  const sign = offMin >= 0 ? "+" : "-";
  const abs = Math.abs(offMin);
  return (
    `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}` +
    `T${pad2(d.getHours())}:${pad2(d.getMinutes())}:00` +
    `${sign}${pad2(Math.floor(abs / 60))}:${pad2(abs % 60)}`
  );
}

/** HTTPS-only URL (mirrors the server `@IsUrl({protocols:['https']})`). */
export function isValidLink(value: string): boolean {
  const v = value.trim();
  if (!/^https:\/\//i.test(v)) return false;
  try {
    const u = new URL(v);
    return u.protocol === "https:" && u.hostname.length > 0;
  } catch {
    return false;
  }
}

/**
 * Validates that page_id is a bare numeric string — matching the server's
 * `@Matches(/^\d+$/)` constraint on `RunCreate.page_id`. Meta page ids from
 * the Admanage-backed dropdown are always numeric, so this does not reject
 * any real page selection but prevents a non-numeric free-text value from
 * reaching the server and getting a 422.
 */
export function isValidPageId(value: string): boolean {
  return /^\d+$/.test(value.trim());
}

/** Translate a fetch error into an actionable Field-level message. */
function admanageErrorMessage(error: unknown, resource: string): string {
  if (error instanceof ApiError) {
    if (error.status === 424) {
      return "Admanage credential unavailable — reconnect in Settings → Credentials.";
    }
    if (error.status === 502) {
      return `Couldn't reach Admanage — try again.`;
    }
  }
  return `Failed to load ${resource}.`;
}

export function Step3LaunchDefaults({
  selectedCount,
  values,
  onChange,
  showErrors,
  productKey,
  disabled = false,
  hideCombine = false,
}: {
  selectedCount: number;
  values: LaunchDefaults;
  onChange: (next: LaunchDefaults) => void;
  showErrors: boolean;
  /**
   * The product key resolved in Step 1 — gates the Admanage queries.
   * When absent the queries are disabled and the selects render in a
   * disabled/loading-not-started state.
   */
  productKey?: string;
  /**
   * Read-only mode: render the chosen values for review but block editing
   * (used on the Preview step where the run is already created from these
   * values). The Admanage queries still run so the selects show the resolved
   * labels, not raw ids.
   */
  disabled?: boolean;
  /**
   * Hide the combine-into-one-ad-set control (2026-07-31): the POV fan-out
   * creates one RUN per group and per-group combined ad sets would race on
   * one name — combine is a single-run feature.
   */
  hideCombine?: boolean;
}) {
  function set<K extends keyof LaunchDefaults>(key: K, value: LaunchDefaults[K]) {
    onChange({ ...values, [key]: value });
  }

  // Controlled search values per Select so the "nothing found" message can
  // distinguish a FILTER no-match ("No results for '<q>'") from a genuinely
  // empty/loading data set (API-empty or in-flight) — Mantine collapses both
  // into a single `nothingFoundMessage`, so we branch on the live query.
  const [pageSearch, setPageSearch] = useState("");
  const [campaignSearch, setCampaignSearch] = useState("");
  const [adsetSearch, setAdsetSearch] = useState("");

  const resolvedProductKey = productKey ?? "";
  const enabled = resolvedProductKey.length > 0;

  // ---- Admanage queries ---------------------------------------------------

  const profilesQuery = useLaunchProfiles(resolvedProductKey, enabled);
  const campaignsQuery = useLaunchCampaigns(resolvedProductKey, enabled);
  const landingPagesQuery = useLaunchLandingPages(resolvedProductKey, enabled && !disabled);
  // Adsets gated on a campaign being chosen.
  const adsetsQuery = useLaunchAdsets(resolvedProductKey, values.target_campaign_id);

  // ---- Option lists -------------------------------------------------------

  // Name-only options — the Facebook `picture_url` from Admanage is a
  // short-lived signed URL that's typically expired by the time the operator
  // opens the wizard, so the icon would render broken. We deliberately omit it.
  // The "· N app ads" suffix (batch 4) counts ads THIS app has launched onto
  // the page — an honest partial signal for the per-page active-ad cap (ads
  // launched outside the app are invisible to it, so it is never phrased as
  // "N of cap"). Omitted at 0 or when the server skipped the aggregate.
  const profileOptions = (profilesQuery.data?.profiles ?? []).map((p) => ({
    value: p.page_id,
    label: p.app_ad_count ? `${p.name} · ${p.app_ad_count} app ads` : p.name,
  }));

  const campaignOptions = (campaignsQuery.data?.campaigns ?? []).map((c) => ({
    value: c.id,
    label: c.name,
  }));

  // The URL dropdown (2026-10-19): the product's pages from the Notion
  // Landing Pages table, offered by URL and shown with their name. Typing
  // still works: Autocomplete never restricts the value to its options.
  const landingPages = landingPagesQuery.data?.landing_pages ?? [];
  const landingPageName = new Map(landingPages.map((p) => [p.url, p.name]));
  const landingPageUrls = [...new Set(landingPages.map((p) => p.url))];

  const adsetOptions = (adsetsQuery.data?.adsets ?? []).map((a) => ({
    value: a.id,
    label: a.name,
  }));

  // ---- Dangling-id self-heal (2026-07-07 #4) -------------------------------
  // A page/campaign/adset VALUE not present in the loaded option list renders
  // as a BLANK Select while still passing the non-empty validation — the
  // operator would launch against an id they never saw (a prefilled campaign
  // that has since ended, a deleted template adset, or a typo'd per-product
  // default page id from config — prefill v2). Once the live options load,
  // clear any value they don't contain (a campaign clear also clears the
  // adset — the same invariant the campaign onChange keeps). Never in the
  // read-only Preview summary: those values belong to an already-created run.
  useEffect(() => {
    if (disabled) return;
    const pageId = values.page_id.trim();
    if (
      pageId &&
      profilesQuery.isSuccess &&
      !profilesQuery.data.profiles.some((p) => p.page_id === pageId)
    ) {
      onChange({ ...values, page_id: "" });
      return;
    }
    const campaignId = values.target_campaign_id.trim();
    if (
      campaignId &&
      campaignsQuery.isSuccess &&
      !campaignsQuery.data.campaigns.some((c) => c.id === campaignId)
    ) {
      onChange({ ...values, target_campaign_id: "", source_adset_id: "" });
      return;
    }
    const adsetId = values.source_adset_id.trim();
    if (
      campaignId &&
      adsetId &&
      adsetsQuery.isSuccess &&
      !adsetsQuery.data.adsets.some((a) => a.id === adsetId)
    ) {
      onChange({ ...values, source_adset_id: "" });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- onChange identity
    // changes per parent render; the guards make re-runs no-ops once healed.
  }, [
    disabled,
    values.page_id,
    values.target_campaign_id,
    values.source_adset_id,
    profilesQuery.isSuccess,
    profilesQuery.data,
    campaignsQuery.isSuccess,
    campaignsQuery.data,
    adsetsQuery.isSuccess,
    adsetsQuery.data,
  ]);

  // ---- Validation ---------------------------------------------------------

  const pageInvalid = showErrors && !isValidPageId(values.page_id);
  const linkInvalid = showErrors && !isValidLink(values.link);
  const campaignInvalid = showErrors && values.target_campaign_id.trim() === "";
  const adsetInvalid = showErrors && values.source_adset_id.trim() === "";

  // ---- Field-level messages -----------------------------------------------

  const pageError = profilesQuery.error
    ? admanageErrorMessage(profilesQuery.error, "pages")
    : pageInvalid
      ? "A Facebook page is required."
      : undefined;

  const campaignError = campaignsQuery.error
    ? admanageErrorMessage(campaignsQuery.error, "campaigns")
    : campaignInvalid
      ? "A target campaign is required."
      : undefined;

  const adsetError = adsetsQuery.error
    ? admanageErrorMessage(adsetsQuery.error, "adsets")
    : adsetInvalid
      ? "A source adset is required."
      : undefined;

  // The "nothing found" message is state-dependent: an active filter query that
  // matches nothing → "No results for '<q>'"; otherwise loading vs API-empty.
  const noMatch = (q: string) => `No results for "${q.trim()}"`;
  const pageNothing = pageSearch.trim()
    ? noMatch(pageSearch)
    : profilesQuery.isLoading
      ? "Loading pages…"
      : "No Facebook pages found.";
  const campaignNothing = campaignSearch.trim()
    ? noMatch(campaignSearch)
    : campaignsQuery.isLoading
      ? "Loading campaigns…"
      : "No active or scheduled campaigns.";
  const adsetNothing = adsetSearch.trim()
    ? noMatch(adsetSearch)
    : adsetsQuery.isLoading
      ? "Loading adsets…"
      : "This campaign has no ad sets.";

  // ---- Render -------------------------------------------------------------

  return (
    <Stack gap="md">
      <Text c="dimmed" fz="sm">
        {selectedCount} ticket(s) selected. These values apply to every selected
        ticket.
      </Text>

      <Stack gap="md" maw={560}>
        {/* ---- Page ----------------------------------------------------- */}
        <Select
          label="Page"
          required
          searchable
          description={
            profilesQuery.error ? undefined : "Facebook page used for the ads."
          }
          placeholder="Choose a page…"
          data={profileOptions}
          value={values.page_id || null}
          onChange={(v) => set("page_id", v ?? "")}
          searchValue={pageSearch}
          onSearchChange={setPageSearch}
          disabled={disabled || !!profilesQuery.error}
          error={pageError}
          nothingFoundMessage={pageNothing}
          comboboxProps={{ withinPortal: true }}
        />

        {/* ---- URL ------------------------------------------------------ */}
        <Autocomplete
          label="URL"
          required
          description={
            landingPages.length > 0
              ? "HTTPS landing URL. Pick a page from Notion's Landing Pages or type one."
              : "HTTPS landing URL."
          }
          placeholder="https://…"
          value={values.link}
          // U3 — use `disabled` (not `readOnly`) so the field greys out to match
          // its sibling Page/Campaign/Adset Selects in the read-only Preview summary.
          disabled={disabled}
          onChange={(v) => set("link", v)}
          data={landingPageUrls}
          // Show every page on open, not only those matching what is typed:
          // the list is short and the typed value is usually a full URL.
          filter={({ options }) => options}
          renderOption={({ option }) => (
            <div>
              <Text fz="sm">{landingPageName.get(option.value) ?? option.value}</Text>
              <Text fz="xs" c="dimmed">
                {option.value}
              </Text>
            </div>
          )}
          error={linkInvalid ? "Enter a valid https:// URL." : undefined}
          autoComplete="off"
          spellCheck={false}
          comboboxProps={{ withinPortal: true }}
        />

        {/* ---- Campaign ------------------------------------------------- */}
        <Select
          label="Campaign"
          required
          searchable
          description={
            campaignsQuery.error ? undefined : "Active or scheduled campaign."
          }
          placeholder="Choose a campaign…"
          data={campaignOptions}
          value={values.target_campaign_id || null}
          onChange={(v) => {
            const next = v ?? "";
            // Selecting a different campaign clears the adset.
            if (next !== values.target_campaign_id) {
              onChange({
                ...values,
                target_campaign_id: next,
                source_adset_id: "",
              });
            } else {
              set("target_campaign_id", next);
            }
          }}
          searchValue={campaignSearch}
          onSearchChange={setCampaignSearch}
          disabled={disabled || !!campaignsQuery.error}
          error={campaignError}
          nothingFoundMessage={campaignNothing}
          comboboxProps={{ withinPortal: true }}
        />

        {/* ---- Adset ---------------------------------------------------- */}
        {/* 2026-10-20: duplicate a template (default) or add the ads to an
            ad set that already exists. */}
        <SegmentedControl
          aria-label="Ad set option"
          fullWidth
          value={values.into_existing_adset ? "existing" : "duplicate"}
          disabled={disabled}
          onChange={(v) =>
            onChange({
              ...values,
              into_existing_adset: v === "existing",
              // One shared ad set is the point of either mode: never both.
              combine_adsets: v === "existing" ? false : values.combine_adsets,
            })
          }
          data={[
            { value: "duplicate", label: "Duplicate an ad set" },
            { value: "existing", label: "Launch into an existing ad set" },
          ]}
        />
        <Select
          label={values.into_existing_adset ? "Ad set to launch into" : "Adset"}
          required
          searchable
          description={
            adsetsQuery.error
              ? undefined
              : !values.target_campaign_id
                ? "Choose a campaign first."
                : values.into_existing_adset
                  ? "Every selected ticket's ads are added to this ad set. Nothing new is created, and the ads follow this ad set's own schedule and budget."
                  : "The template adset duplicated per concept."
          }
          placeholder={
            values.target_campaign_id
              ? "Choose an adset…"
              : "Choose a campaign first"
          }
          data={adsetOptions}
          value={values.source_adset_id || null}
          onChange={(v) => set("source_adset_id", v ?? "")}
          searchValue={adsetSearch}
          onSearchChange={setAdsetSearch}
          disabled={
            disabled || !values.target_campaign_id || !!adsetsQuery.error
          }
          error={adsetError}
          nothingFoundMessage={adsetNothing}
          comboboxProps={{ withinPortal: true }}
        />

        {/* ---- Persona name ([NAME] swap, optional) ---------------------- */}
        <TextInput
          label="Persona name"
          description="Optional — replaces [NAME] placeholders in the copy with this first name. Leave empty to keep the strict placeholder check."
          placeholder="e.g. Sarah"
          value={values.name_swap}
          disabled={disabled}
          onChange={(e) => set("name_swap", e.currentTarget.value)}
          maxLength={80}
          autoComplete="off"
          spellCheck={false}
        />

        {/* Go-live time, bid, goal and combine only shape a NEW ad set. */}
        {!values.into_existing_adset && (
        <>
        {/* ---- Go-live time (scheduled launch, #8 — always on) ----------- */}
        <TextInput
          label="Go-live time"
          required
          type="datetime-local"
          description="Adsets land in Meta's Scheduled state and go live at this time (bypasses the per-page active-ad cap). Defaults to ~1.5 hours ahead."
          value={values.start_time}
          disabled={disabled}
          onChange={(e) => set("start_time", e.currentTarget.value)}
          error={
            showErrors && !isValidStartTime(values.start_time)
              ? "Pick a go-live time in the future."
              : undefined
          }
        />

        {/* ---- Bid for this launch (2026-07-17) -------------------------- */}
        <Select
          label="Bid for this launch"
          description="Product setting uses the configured bid; No bid launches without a cap (for campaigns that must not carry one); Custom overrides the amount for this launch only."
          data={[
            { value: "product", label: "Product setting (default)" },
            { value: "none", label: "No bid — inherit the campaign" },
            { value: "custom", label: "Custom amount" },
          ]}
          value={values.bid_mode}
          disabled={disabled}
          onChange={(v) => set("bid_mode", (v ?? "product") as LaunchDefaults["bid_mode"])}
          comboboxProps={{ withinPortal: true }}
        />
        {values.bid_mode === "custom" && (
          <TextInput
            label="Custom bid / cost-cap amount"
            description="In the ad account's currency (e.g. 28 = $28.00)."
            placeholder="e.g. 28"
            value={values.bid_amount}
            disabled={disabled}
            onChange={(e) => set("bid_amount", e.currentTarget.value)}
            error={
              showErrors && !isValidBid(values)
                ? "Enter a positive amount."
                : undefined
            }
          />
        )}

        {/* ---- Optimization goal for this launch (2026-07-20) ------------ */}
        {/* 2026-10-29: a scheduled launch takes each ad set's goal from its own
            campaign, so this is only the fallback (shown per ad set in Step 4). */}
        <Select
          label="Fallback optimization goal"
          description="Scheduled launches give each ad set the goal of the campaign it goes into, and the preview shows it. This is used only for a campaign with no ad sets yet. Launching now or as a draft copies the template ad set, or, for a campaign with another goal, that campaign's newest active ad set."
          data={[
            { value: "product", label: "Product setting (default)" },
            { value: "OFFSITE_CONVERSIONS", label: "Conversions (OFFSITE_CONVERSIONS)" },
            { value: "VALUE", label: "Highest value (VALUE)" },
          ]}
          value={values.opt_goal}
          disabled={disabled}
          onChange={(v) => set("opt_goal", (v ?? "product") as LaunchDefaults["opt_goal"])}
          comboboxProps={{ withinPortal: true }}
        />

        {/* ---- Combine into one ad set (2026-07-31, retargeting) --------- */}
        {!hideCombine && (
          <>
            <Switch
              label="Combine into one ad set"
              description="All selected tickets' ads launch into a single ad set (for retargeting) instead of one ad set per concept. Ad names keep each ticket's own naming."
              checked={values.combine_adsets}
              disabled={disabled}
              onChange={(e) => set("combine_adsets", e.currentTarget.checked)}
            />
            {values.combine_adsets && (
              <TextInput
                label="Combined ad set name"
                required
                description="The exact ad set name. If an ad set with this name already exists in the account, the ads are ADDED to it; otherwise it is created."
                placeholder="e.g. MD_RT_WK31"
                value={values.combine_adset_name}
                disabled={disabled}
                onChange={(e) => set("combine_adset_name", e.currentTarget.value)}
                maxLength={200}
                autoComplete="off"
                spellCheck={false}
                error={
                  showErrors && values.combine_adset_name.trim() === ""
                    ? "Name the combined ad set."
                    : undefined
                }
              />
            )}
          </>
        )}
        </>
        )}
      </Stack>
    </Stack>
  );
}
